import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PieChart, Pie, Cell, Label as RechartsLabel } from "recharts";
import { TextSearch } from "lucide-react";

import { SearchSlash } from "lucide-react";

import * as Yup from "yup";

import { DataTableSchedulings } from "@/components/common";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
  Skeleton,
  Button,
  PageHeader,
  Label,
  MultiSelect,
} from "@/components/ui";

import {
  useDownloadReportSchedulingsToExcelMutation,
  useDownloadReportSchedulingsToPDFMutation,
  useFetchEmployeesNoPaginateQuery,
  useFetchPatientsNoPaginateQuery,
  useFetchProceduresQuery,
  useFetchReportSchedulesQuery,
  useRequestReportSchedulingPDFMutation,
  useToast,
} from "@/hooks";
import { ChartScheduleData } from "@/types";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { formatDateToBackend } from "@/utils";
interface HandleFilterReportScheduleProps {
  proceduresId: string;
  date_start: string;
  date_end: string;
  status: string;
  patientsId: string;
  employeesId: string;
  payment_history: string;
}

export function SchedulingsReports() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const today = new Date();
  const firstDayOfMonth = startOfMonth(today);
  const lastDayOfMonth = endOfMonth(today);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [searchParams, setSearchParams] = useSearchParams();

  const page = searchParams.get("page");
  const proceduresId = searchParams.get("proceduresId");
  const perPageParam = searchParams.get("per_page");
  const dateStart = searchParams.get("date_start");
  const dateEnd = searchParams.get("date_end");
  const status = searchParams.get("status");
  const patientsId = searchParams.get("patientsId");
  const employeesId = searchParams.get("employeesId");
  const payment_history = searchParams.get("payment_history");

  const [perPage, setPerPage] = useState<number>(
    perPageParam ? Number(perPageParam) : 10
  );

  const { mutateAsync: downloadExcel, isLoading: isLoadingDownloadExcel } =
    useDownloadReportSchedulingsToExcelMutation({
      page: page || "1",
      proceduresId: proceduresId || "",
      date_start: dateStart || "",
      date_end: dateEnd || "",
      status: status || "",
      patientsId: patientsId || "",
      employeesId: employeesId || "",
    });
  const { mutateAsync: downloadPDF, isLoading: isLoadingDownloadPDF } =
    useDownloadReportSchedulingsToPDFMutation({
      page: page || "1",
      proceduresId: proceduresId || "",
      date_start: dateStart || "",
      date_end: dateEnd || "",
      status: status || "",
      patientsId: patientsId || "",
      employeesId: employeesId || "",
    });
  const { mutateAsync: requestPDF, isLoading: isLoadingRequestPDF } = useRequestReportSchedulingPDFMutation();

  const { data: patientsData, isLoading: isLoadingPatients } =
    useFetchPatientsNoPaginateQuery();
  const { data: employeesData, isLoading: isLoadingEmployees } =
    useFetchEmployeesNoPaginateQuery();
  const { data: proceduresData, isLoading: isLoadingProcedures } =
    useFetchProceduresQuery({ perPage: "100" });

  const {
    data: schedulingsForList,
    isLoading: isLoadingSchedulings,
    refetch,
  } = useFetchReportSchedulesQuery({
    page: page || "1",
    proceduresId: proceduresId || "",
    date_start: dateStart || "",
    date_end: dateEnd || "",
    status: status || "",
    patientsId: patientsId || "",
    employeesId: employeesId || "",
    payment_history: payment_history || "",
    per_page: perPageParam,
  });

  const initialValues: HandleFilterReportScheduleProps = {
    proceduresId: "",
    date_start: format(firstDayOfMonth, 'dd/MM/yyyy') || "",
    date_end: format(lastDayOfMonth, 'dd/MM/yyyy') || "",
    status: "",
    patientsId: "",
    employeesId: "",
    payment_history: "",
  };

  const { handleSubmit, resetForm, ...formik } =
    useFormik<HandleFilterReportScheduleProps>({
      initialValues,
      validationSchema: Yup.object().shape({
        proceduresId: Yup.string().optional(),
        date_start: Yup.string().optional(),
        date_end: Yup.string().optional(),
        status: Yup.string().optional(),
        patientsId: Yup.string().optional(),
        employeesId: Yup.string().optional(),
        payment_history: Yup.string().optional(),
      }),

      onSubmit: async (data) => {
        await handlefilterFinance(data);
      },
    });

  async function handlefilterFinance(values: HandleFilterReportScheduleProps) {
    try {
      if (values.proceduresId) { queryParams.set("proceduresId", values.proceduresId); }
      if (values.date_start) { queryParams.set("date_start", formatDateToBackend(values.date_start)) }
      if (values.date_end) { queryParams.set("date_end", formatDateToBackend(values.date_end)); }
      if (values.status) { queryParams.set("status", values.status); }
      if (values.payment_history) { queryParams.set("payment_history", values.payment_history); }
      if (values.patientsId) { queryParams.set("patientsId", values.patientsId); }
      if (values.employeesId) { queryParams.set("employeesId", values.employeesId); }
      await navigate({ search: queryParams.toString() });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t("MESSAGE.error"),
        variant: "destructive",
      });
    }
  }

  function clearFilter() {
    queryParams.delete("proceduresId");
    queryParams.delete("date_start");
    queryParams.delete("date_end");
    queryParams.delete("status");
    queryParams.delete("patientsId");
    queryParams.delete("employeesId");
    queryParams.delete("per_page");
    formik.setFieldValue("employeesId", "");
    formik.setFieldValue("patientsId", "");
    formik.setFieldValue("proceduresId", "");

    if (formik.values.date_start) { queryParams.set("date_start", formatDateToBackend(formik.values.date_start)) }
    if (formik.values.date_end) { queryParams.set("date_end", formatDateToBackend(formik.values.date_end)); }

    navigate({ search: queryParams.toString() });
    resetForm();
  }

  function hasFiltersApplied() {
    const relevantParams = [
      "proceduresId",
      "date_start",
      "date_end",
      "status",
      "patientsId",
      "employeesId",
    ];
    return (
      Object.values(formik.values).some((value) => value) ||
      relevantParams.some((param) => queryParams.has(param))
    );
  }

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("per_page", newPerPage.toString());
    setSearchParams(newSearchParams);
  };

  const handlePrint = async () => {
    try {
      const blob = await requestPDF({
        page: page || "1",
        proceduresId: proceduresId || "",
        date_start: dateStart || "",
        date_end: dateEnd || "",
        status: status || "",
        patientsId: patientsId || "",
        employeesId: employeesId || "",
        payment_history: payment_history || "",
        per_page: perPageParam,
      });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            document.body.removeChild(iframe);
          }, 1000);
        }, 500);
      };
    } catch (error) {
      console.error("Erro ao gerar PDF para impressão:", error);
    }
  };

  useEffect(() => {
    const shouldApplyDefaultDateFilter = !dateStart && !dateEnd;

    if (shouldApplyDefaultDateFilter) {
      handlefilterFinance(initialValues)
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [
    refetch,
    page,
    proceduresId,
    dateStart,
    dateEnd,
    status,
    patientsId,
    employeesId,
    payment_history,
  ]);

  return (
    <section className="space-y-7">
      <PageHeader eyebrow={t("LABEL.reports")} title={t("LABEL.appointment report")} />

      <main className="space-y-10">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-x-2 gap-y-4">
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.from")}</Label>
              <DatePicker
                {...formik.getFieldProps("date_start")}
                isInvalid={
                  formik.touched.date_start && !!formik.errors.date_start
                }
                isValid={formik.touched.date_start && !formik.errors.date_start}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.to")}</Label>
              <DatePicker
                {...formik.getFieldProps("date_end")}
                isInvalid={formik.touched.date_end && !!formik.errors.date_end}
                isValid={formik.touched.date_end && !formik.errors.date_end}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.status")}</Label>
              <Select
                {...formik.getFieldProps("status")}
                onValueChange={(value) => {
                  formik.setFieldValue("status", value);
                }}
                defaultValue={formik.values.status}
                key={formik.values.status}
              >
                <SelectTrigger
                  isInvalid={
                    formik.touched.status && formik.errors.status !== undefined
                  }
                  isValid={
                    formik.touched.status && formik.errors.status === undefined
                  }
                >
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">{t("LABEL.all")}</SelectItem>
                    <SelectItem value="pending">
                      {t("LABEL.pending")}
                    </SelectItem>
                    <SelectItem value="confirmed">
                      {t("LABEL.confirmed")}
                    </SelectItem>
                    <SelectItem value="finished">
                      {t("LABEL.finished")}
                    </SelectItem>
                    <SelectItem value="no_show">Não compareceu</SelectItem>
                    <SelectItem value="canceled">
                      {t("LABEL.canceled")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.status payment")}</Label>

              <Select
                {...formik.getFieldProps("payment_history")}
                onValueChange={(value) => {
                  formik.setFieldValue("payment_history", value);
                }}
                defaultValue={formik.values.payment_history}
                key={formik.values.payment_history}
              >
                <SelectTrigger
                  isInvalid={formik.touched.payment_history && formik.errors.payment_history}
                  isValid={formik.touched.payment_history && !formik.errors.payment_history}
                >
                  <SelectValue placeholder="Selecione o status de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">
                      {t("LABEL.all")}
                    </SelectItem>
                    <SelectItem value="1">
                      {t("LABEL.with financial")}
                    </SelectItem>
                    <SelectItem value="0">
                      {t("LABEL.no financial")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.collaborator")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(employeesData?.map((employee) => ({
                    value: employee.id,
                    label: employee.person.name,
                  })) || []),
                ]}
                loading={isLoadingEmployees}
                placeholder="Selecione um colaborador..."
                width="300px"
                isInvalid={
                  formik.touched.employeesId && formik.errors.employeesId
                }
                isValid={
                  formik.touched.employeesId && !formik.errors.employeesId
                }
                value={formik.values.employeesId}
                onChange={(value) => formik.setFieldValue("employeesId", value)}
                onBlur={() => formik.handleBlur("employeesId")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.patient")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(patientsData?.map((patient) => ({
                    value: patient.id,
                    label: patient.person.name,
                  })) || []),
                ]}
                loading={isLoadingPatients}
                placeholder="Selecione um paciente..."
                width="300px"
                isInvalid={
                  formik.touched.patientsId && formik.errors.patientsId
                }
                isValid={formik.touched.patientsId && !formik.errors.patientsId}
                value={formik.values.patientsId}
                onChange={(value) => formik.setFieldValue("patientsId", value)}
                onBlur={() => formik.handleBlur("patientsId")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("LABEL.procedure")}</Label>
              <MultiSelect
                options={[
                  { value: "all", label: t("LABEL.all") },
                  ...(proceduresData?.items.map((procedure) => ({
                    value: procedure.id,
                    label: procedure.name,
                  })) || []),
                ]}
                loading={isLoadingProcedures}
                placeholder="Selecione um procedimento..."
                width="300px"
                isInvalid={
                  formik.touched.proceduresId && formik.errors.proceduresId
                }
                isValid={
                  formik.touched.proceduresId && !formik.errors.proceduresId
                }
                value={formik.values.proceduresId}
                onChange={(value) =>
                  formik.setFieldValue("proceduresId", value)
                }
                onBlur={() => formik.handleBlur("proceduresId")}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button className="items-center px-10 h-10">
                <TextSearch size={14} /> {t("LABEL.consult")}
              </Button>
              {hasFiltersApplied() && (
                <Button type="button" onClick={clearFilter} variant="secondary">
                  <SearchSlash size={14} /> {t("LABEL.to clean")}
                </Button>
              )}
            </div>
          </div>
        </form>

        {!isLoadingSchedulings && schedulingsForList ? (
          <div className="space-y-7">
            <ChartReportSchedule
              chartReportScheduleData={schedulingsForList.chart}
            />
            <div className="space-y-3">
              <div className="flex items-center justify-end gap-x-2">
                <Button
                  variant="secondary"
                  onClick={handlePrint}
                  isLoading={isLoadingRequestPDF}>
                  {t("LABEL.print")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => downloadExcel()}
                  isLoading={isLoadingDownloadExcel}
                >
                  {t("LABEL.export excel")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => downloadPDF()}
                  isLoading={isLoadingDownloadPDF}
                >
                  {t("LABEL.export pdf")}
                </Button>
              </div>
              <DataTableSchedulings setPerPage={handlePerPageChange} perPage={perPage} data={schedulingsForList} />
            </div>
          </div>
        ) : (
          <div>
            <Skeleton className="w-full h-[70vh]" />
          </div>
        )}
      </main>
    </section>
  );
}

interface ChartReportScheduleProps {
  chartReportScheduleData: ChartScheduleData;
}

/** Exported for `schedulings-chart.test.tsx`: the SVG owns the report's ink. */
export function ChartReportSchedule({
  chartReportScheduleData,
}: ChartReportScheduleProps) {
  const { t } = useTranslation();

  // P-11: the four slices are painted with the same semantic tokens the rest
  // of the app uses for these states, never with a hex picked here. `ink` is
  // the label colour chosen per slice to clear the 4.5:1 floor of the design
  // system (§6): white text failed it on the amber and the teal.
  const chartData = [
    {
      fillClass: "fill-app-warning",
      dotClass: "bg-app-warning",
      inkClass: "fill-app-fg-strong",
      label: t(`LABEL.pending`),
      value: chartReportScheduleData.pending.percentage,
      number: chartReportScheduleData.pending.value,
    },
    {
      fillClass: "fill-app-success",
      dotClass: "bg-app-success",
      inkClass: "fill-app-fg-strong",
      label: t(`LABEL.confirmed`),
      value: chartReportScheduleData.confirmed.percentage,
      number: chartReportScheduleData.confirmed.value,
    },
    {
      fillClass: "fill-app-info",
      dotClass: "bg-app-info",
      inkClass: "fill-app-fg-strong",
      label: t(`LABEL.finished`),
      value: chartReportScheduleData.finished.percentage,
      number: chartReportScheduleData.finished.value,
    },
    {
      fillClass: "fill-app-danger",
      dotClass: "bg-app-danger",
      inkClass: "fill-brand-fg",
      label: t(`LABEL.canceled`),
      value: chartReportScheduleData.canceled.percentage,
  number: chartReportScheduleData.canceled.value,
},
...(chartReportScheduleData.no_show ? [{
  fillClass: "fill-app-fg-muted",
  dotClass: "bg-app-fg-muted",
  inkClass: "fill-app-fg-strong",
  label: "Não compareceu",
  value: chartReportScheduleData.no_show.percentage,
  number: chartReportScheduleData.no_show.value,
}] : []),
  ];

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index }: any) => {
    const item = chartData[index];
    if (!item || item.value === 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        className={`font-sans text-xs font-semibold tabular-nums ${item.inkClass}`}
      >
        <tspan x={x} dy="-1em">{item.label}</tspan>
        <tspan x={x} dy="1.2em">{item.number}</tspan>
        <tspan x={x} dy="1.2em">{item.value.toFixed(2)}%</tspan>
      </text>
    );
  };

  return (
    <div className="w-full py-7 px-4">
      <div className="flex justify-center items-center gap-3">
        <div className="w-[280px] h-[280px]">
          <PieChart width={280} height={280}>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={130}
              strokeWidth={0}
              label={renderCustomLabel}
              labelLine={false}
              // The design system keeps motion at 150ms transitions; a pie
              // sweeping itself open is decoration, and it also delayed the
              // labels until the animation reported it had finished.
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={index} className={entry.fillClass} />
              ))}
              <RechartsLabel
                value={chartReportScheduleData.total.toString()}
                position="center"
                className="font-display text-[32px] font-medium tabular-nums fill-app-fg-strong"
              />
            </Pie>
          </PieChart>
        </div>

        <div className="cursor-default grid grid-cols-1">
          {chartData.map((item, index) => (
            <div key={index} className="inline-block mx-2 my-0">
              <div
                className={`w-2 h-2 inline-block mr-2 rounded-full ${item.dotClass}`}
              />
              <span className="text-[14px] leading-[16.94px] text-app-fg-muted font-[400]">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
